import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';

/**
 * Utility class to block elements on a form from being clicked or
 * block left and right mouse click and keyboard key inputs across the entire app
 */
export class BlockUtils {
	private static getElementScope(classicForm: HTMLElement): HTMLElement {
		// NOTE(Jake): 2018-09-04
		// If the form is inside a modal / ux-dialog element, use the <ux-dialog>
		// element to find <input>, <select> and <button> elements.
		//
		// Ideally the correct solution would be to have all these elements underneath
		// a <form> tag, and then iterate over the "elements" property.
		// ie. https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/elements
		//
		let scope = classicForm;
		document.querySelectorAll('ux-dialog').forEach((el) => {
			if (el.contains(scope)) {
				scope = el as HTMLElement;
			}
		});
		return scope;
	}

	public static isAppDisabledOrLoading(
		appOverlayElement?: HTMLElement,
	): boolean {
		if (!appOverlayElement) {
			appOverlayElement = BlockUtils.getAppOverlayElement();
		}
		if (!appOverlayElement) {
			// the app is loading
			return true;
		}
		// If the overlay is shown, the app is disabled.
		return appOverlayElement.classList.contains('a-app-overlay--shown');
	}

	private static stopEventPropagation = (event) => {
		event.stopImmediatePropagation();
		event.preventDefault();
	};

	/**
	 * Toggles left and right mouse click and keyboard key inputs on the entire app
	 * @param {boolean} disableInput When true shows an invisible overlay on the App to disable left mouse clicks,
	 * disables right mouse click events on the App and disables keydown events on the Document
	 * (keyboard-shortcut.ts has keydown events registered on the Document).
	 *
	 * If input has been disabled for longer than 0.5 seconds a 'processing' spinner will be shown and the background
	 */
	public static toggleAppInput(disableInput: boolean) {
		const appOverlayElement = BlockUtils.getAppOverlayElement();
		const isDisabledOrLoading =
			BlockUtils.isAppDisabledOrLoading(appOverlayElement);

		if (disableInput && isDisabledOrLoading) {
			Logger.warn(
				'Should not disable inputs if App is already disabled.',
			);
			return;
		} else if (!disableInput && !isDisabledOrLoading) {
			Logger.warn('Should not enable inputs if App is already enabled');
			return;
		}

		appOverlayElement.classList.toggle(
			'a-app-overlay--shown',
			disableInput,
		);
		window.requestAnimationFrame(() => {
			// activate transition (need to add a new class after display:none was removed)
			appOverlayElement.classList.toggle(
				'a-app-overlay-transition',
				disableInput,
			);
		});
		if (disableInput) {
			KeyboardShortcut.setDisableShortcuts(true);
			window.document.addEventListener(
				'keydown',
				this.stopEventPropagation,
			);
			appOverlayElement.addEventListener(
				'contextmenu',
				BlockUtils.preventEventDefault,
			);
			BlockUtils.hideAllVisibleTooltips();
		} else {
			KeyboardShortcut.setDisableShortcuts(false);
			window.document.removeEventListener(
				'keydown',
				this.stopEventPropagation,
			);
			appOverlayElement.removeEventListener(
				'contextmenu',
				BlockUtils.preventEventDefault,
			);
		}
	}

	private static preventEventDefault: EventListener = (event) => {
		event.preventDefault();
	};

	private static getAppOverlayElement(): HTMLElement {
		return document.querySelector('.a-app-overlay');
	}

	private static hideAllVisibleTooltips() {
		// NOTE: Jake: 2018-11-20 - WEBUI-1152
		// Find any currently visible tooltips and hide them gracefully.
		document
			.querySelectorAll('.tooltip[role="tooltip"]')
			.forEach((tooltipEl) => {
				const id = tooltipEl.getAttribute('id');
				if (!id) {
					throw new Error(
						'Expected .tooltip element to have an id attribute.',
					);
				}
				const el = document.querySelector(
					'[aria-describedby="' + id + '"]',
				);
				if (el) {
					// NOTE: Jake: 2018-11-20 - WEBUI-1152
					// Call the 'hide' function so the tooltip goes away with a nice fadeout.
					// I tried `el.remove()` but that made the tooltip not work nicely when you tried
					// to hover over it again.
					//
					// I also tried to make it only affect disabled buttons, but that didn't seem to work...
					// It must take a while for the CSS "display: none;" properties to take affect.
					$(el).tooltip('hide');
				}
			});
	}
}
